import { Row, Col, Button, Form, Alert } from 'react-bootstrap';
import { useState, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import useDynamicHeight from '../../hooks/useDynamicHeight';
import { useGetDattoSiteDevicesQuery } from '../../slices/dattoRmmApiSlice';

const SiteDattoRMM = ({ site }) => {
    const [quickFilterText, setQuickFilterText] = useState('');
    const gridRef = useRef();

    const { data: devices, isLoading, error } = useGetDattoSiteDevicesQuery(site.DattoUID, {
        skip: !site?.DattoUID, // Skip query if DattoUID is not set
    });
    
    const [gridContainerRef, gridHeight] = useDynamicHeight([devices]);

    const onGridReady = (params) => {
        gridRef.current = params.api;
    };

    const onQuickFilterChange = (event) => {
        setQuickFilterText(event.target.value);
        if (gridRef.current) {
            gridRef.current.setQuickFilter(event.target.value);
        }
    };

    const onExportClick = useCallback(() => {
        if (gridRef.current) {
            const fileName = site.name + '_datto_rmm_devices_' + Date.now() + ".csv";
            gridRef.current.exportDataAsCsv({ fileName: fileName });
        }
    }, [site]);

    const [colDefs] = useState([
        {
            headerName: "Connect",
            field: "webRemoteURL",
            width: 120,
            cellRenderer: (params) => {
                const online = params.data?.online;
                const url = params.data?.webRemoteURL || params.data?.webRemoteUrl;
                if (online && url) {
                    return (
                        <a 
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn-connect-green"
                            style={{ 
                                padding: '4px 12px', 
                                fontSize: '12px', 
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                height: '28px',
                                borderRadius: '6px',
                                marginTop: '6px'
                            }}
                        >
                            Connect
                        </a>
                    );
                }
                return null;
            }
        },
        { field: "hostname", headerName: "Hostname", sortable: true, filter: true },
        { field: "lastLoggedInUser", headerName: "Last Logged In User", sortable: true, filter: true },
        { field: "intIpAddress", headerName: "Internal IP", sortable: true, filter: true },
        { field: "extIpAddress", headerName: "External IP", sortable: true, filter: true },
        { field: "operatingSystem", headerName: "Operating System", sortable: true, filter: true },
        {
            field: "online",
            headerName: "Status",
            sortable: true,
            filter: true,
            cellRenderer: (params) => {
                const online = params.value;
                return (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: '100%' }}>
                        <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: online ? '#2ed573' : '#ff4757',
                            display: 'inline-block'
                        }}></span>
                        <span>{online ? 'Online' : 'Offline'}</span>
                    </div>
                );
            }
        },
        {
            field: "lastSeen",
            headerName: "Last Seen",
            sortable: true,
            cellRenderer: (params) => {
                if (!params.value) return 'N/A';
                try {
                    const date = new Date(params.value);
                    return date.toLocaleString();
                } catch (e) {
                    return params.value;
                }
            }
        }
    ]);

    if (!site?.DattoUID) {
        return (
            <Alert variant="warning" className="mt-3">
                <strong>Datto UID not configured!</strong> Please add the Datto UID for this site under the Site Info tab to pull devices from Datto RMM.
            </Alert>
        );
    }

    return (
        <>
            {isLoading ? (
                <div className="text-center py-5">
                    <h2 className="text-white">Loading Datto RMM Devices...</h2>
                </div>
            ) : error ? (
                <Alert variant="danger" className="mt-3">
                    <strong>Error fetching Datto devices:</strong> {error?.data?.message || error.error || "Could not connect to Datto RMM."}
                </Alert>
            ) : (
                <>
                   

                    <Row>
                        <Col sm={3}>
                            <Form.Control
                                type="text"
                                placeholder="Filter"
                                value={quickFilterText}
                                onChange={onQuickFilterChange}
                                className="mb-3"
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <div ref={gridContainerRef} className="ag-theme-alpine" style={{ height: gridHeight }} >
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={devices}
                                    columnDefs={colDefs}
                                    suppressExcelExport={true}
                                    onGridReady={onGridReady}
                                    quickFilterText={quickFilterText}
                                />
                            </div>
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
};

export default SiteDattoRMM;
